import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Mail, Calendar, CreditCard, Hash } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function ThankYouPage({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const [paymentData, setPaymentData] = useState(null);
  const [checkComplete, setCheckComplete] = useState(false);
  const checkAttempts = useRef(0);
  // Ref to track if we've already consumed localStorage (protects against StrictMode double-invocation)
  const hasConsumedStorage = useRef(false);
  // Ref to store the payment data so it persists across StrictMode re-runs
  const cachedPaymentData = useRef(null);
  // Track if analytics has been fired
  const analyticsFired = useRef(false);

  // Fire analytics when the page mounts with payment data
  const fireAnalytics = (data) => {
    if (analyticsFired.current) return;
    analyticsFired.current = true;
    
    console.log('ThankYouPage: Firing analytics on /thank-you URL', {
      url: window.location.href,
      pathname: window.location.pathname,
      paymentData: data
    });
    
    // Fire Meta Pixel Purchase event on the /thank-you URL
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: data.amount || 0,
        currency: 'INR',
        content_name: data.plan_name || 'StudyClaw Subscription',
        content_type: 'product',
        content_ids: [data.payment_id || 'subscription'],
        order_id: data.order_id || data.razorpay_order_id
      });
      console.log('ThankYouPage: Meta Pixel Purchase event fired');
    }
    
    // Fire Google Analytics purchase event if available
    if (window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: data.order_id || data.razorpay_order_id,
        value: data.amount || 0,
        currency: 'INR',
        items: [{
          item_name: data.plan_name || 'StudyClaw Subscription',
          price: data.amount || 0
        }]
      });
      console.log('ThankYouPage: Google Analytics purchase event fired');
    }
  };

  useEffect(() => {
    const checkPaymentData = () => {
      // Check for preview mode (for testing/demo purposes)
      const urlParams = new URLSearchParams(window.location.search);
      const isPreview = urlParams.get('preview') === 'true';
      
      if (isPreview) {
        // Demo mode - show sample data
        const demoData = {
          plan_name: 'Pro',
          amount: 1529,
          student_name: 'Demo Student',
          billing_cycle: '6months',
          payment_id: 'demo_pay_123',
          order_id: 'demo_order_123'
        };
        setPaymentData(demoData);
        setCheckComplete(true);
        fireAnalytics(demoData);
        return true;
      }
      
      // PRIORITY 1: Check React Router location.state (passed from navigation)
      if (location.state && (location.state.plan_name || location.state.amount)) {
        console.log('ThankYouPage: Using data from location.state', location.state);
        setPaymentData(location.state);
        setCheckComplete(true);
        fireAnalytics(location.state);
        
        // Also cache it in case of re-renders
        cachedPaymentData.current = location.state;
        hasConsumedStorage.current = true;
        
        // Clear localStorage if any exists
        localStorage.removeItem('studyclaw_payment_completed');
        localStorage.removeItem('studyclaw_payment_data');
        return true;
      }
      
      // If we already consumed and cached the data (StrictMode re-run), use cached version
      if (hasConsumedStorage.current && cachedPaymentData.current) {
        setPaymentData(cachedPaymentData.current);
        setCheckComplete(true);
        return true;
      }
      
      // PRIORITY 2: Check localStorage (fallback for older flow)
      const paymentJustCompleted = localStorage.getItem('studyclaw_payment_completed');
      const storedPaymentData = localStorage.getItem('studyclaw_payment_data');
      
      console.log('ThankYouPage: Checking localStorage:', { 
        paymentJustCompleted, 
        hasData: !!storedPaymentData, 
        attempt: checkAttempts.current, 
        alreadyConsumed: hasConsumedStorage.current 
      });
      
      if (paymentJustCompleted === 'true' && storedPaymentData) {
        try {
          const data = JSON.parse(storedPaymentData);
          
          // Cache the data before clearing localStorage
          cachedPaymentData.current = data;
          hasConsumedStorage.current = true;
          
          setPaymentData(data);
          fireAnalytics(data);
          
          // Clear the flags after successfully reading to prevent reuse on refresh
          localStorage.removeItem('studyclaw_payment_completed');
          localStorage.removeItem('studyclaw_payment_data');
          
          setCheckComplete(true);
          return true;
        } catch (e) {
          console.error('Failed to parse payment data:', e);
        }
      }
      return false;
    };

    // Initial check
    if (checkPaymentData()) {
      return;
    }

    // Retry a few times with delay (in case localStorage is being set)
    const retryCheck = () => {
      checkAttempts.current += 1;
      if (checkAttempts.current > 5) {
        // Max retries reached, redirect to dashboard
        // But only if we haven't already consumed valid data
        if (!hasConsumedStorage.current) {
          console.log('No payment data found after retries, redirecting to dashboard');
          onNavigate('dashboard');
        }
        return;
      }
      
      if (!checkPaymentData()) {
        setTimeout(retryCheck, 200);
      }
    };

    // Start retry after initial delay
    const timer = setTimeout(retryCheck, 200);
    
    return () => clearTimeout(timer);
  }, [onNavigate, location.state]);

  // Show loading while checking
  if (!checkComplete || !paymentData) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  const calculateValidUntil = () => {
    if (!paymentData) return '';
    
    const today = new Date();
    let months = 1; // Default monthly
    
    const planName = (paymentData.plan_name || '').toLowerCase();
    if (planName.includes('6') || planName.includes('six')) {
      months = 6;
    } else if (planName.includes('12') || planName.includes('year') || planName.includes('annual')) {
      months = 12;
    }
    
    const validUntil = new Date(today);
    validUntil.setMonth(validUntil.getMonth() + months);
    
    return validUntil.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleSetGoal = () => {
    // Navigate to dashboard - this will trigger the Score Contract onboarding modal
    onNavigate('dashboard');
  };

  const studentName = user?.name || paymentData?.student_name || 'Student';
  const planName = paymentData?.plan_name || 'Pro';
  const amount = paymentData?.amount || 299;

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Animated Checkmark */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 15,
            delay: 0.1 
          }}
          className="flex justify-center mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            >
              <CheckCircle className="w-16 h-16 text-emerald-400" strokeWidth={1.5} />
            </motion.div>
          </div>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-6"
        >
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-3">
            You&apos;re in, {studentName}! 🎉
          </h1>
          <p className="text-slate-400 text-lg">
            Your StudyClaw <span className="text-violet-400 font-semibold">{planName}</span> is now active.
            <br />Let&apos;s get your first exam score up.
          </p>
        </motion.div>

        {/* Plan Confirmation Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-900/50 border-2 border-violet-500/30 rounded-2xl p-6 mb-8"
        >
          <h2 className="text-sm font-semibold text-violet-400 uppercase tracking-wide mb-4">
            Order Confirmed
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-slate-400">Plan</span>
              </div>
              <span className="text-white font-semibold">{planName}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">₹</span>
                </div>
                <span className="text-slate-400">Amount Paid</span>
              </div>
              <span className="text-white font-semibold">₹{amount.toLocaleString('en-IN')}</span>
            </div>

            {/* Transaction ID - shown if available */}
            {(paymentData?.payment_id || paymentData?.razorpay_payment_id) && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-slate-400">Transaction ID</span>
                </div>
                <span className="text-white font-mono text-sm">
                  {(paymentData?.payment_id || paymentData?.razorpay_payment_id || '').slice(-12)}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="text-slate-400">Valid Until</span>
              </div>
              <span className="text-white font-semibold">{calculateValidUntil()}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-2 text-slate-500 text-sm">
            <Mail className="w-4 h-4" />
            <span>Receipt will be sent to your email</span>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <button
            onClick={handleSetGoal}
            data-testid="set-goal-btn"
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-4 font-semibold text-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-violet-500/25"
          >
            Set My First Goal
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Subtle footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-slate-600 text-xs mt-6"
        >
          Questions? Email us at support@studyclaw.io
        </motion.p>
      </div>
    </div>
  );
}
